/**
 * Lead Statistics Cards Component
 * Displays key metrics with click-to-filter functionality
 * Uses server-side card counts for accurate totals across all pages
 */
import React from 'react';
import { Users, TrendingUp, MessageCircle, Bell, Link2, CheckCircle, Clock } from 'lucide-react';

// Summary Card Component - Compact version for sales dashboard
const SummaryCard = ({ title, value, subValue, subLabel, icon: Icon, color, onClick, active }) => (
  <div 
    className={`rounded-xl border bg-white p-4 hover:shadow-lg transition-all duration-300 cursor-pointer ${active ? 'ring-2 ring-blue-500 border-blue-500' : ''}`}
    onClick={onClick}
    data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center gap-3">
      <div className={`p-2.5 rounded-xl bg-gradient-to-r ${
        color?.includes('blue') ? 'from-blue-500 to-blue-600' : 
        color?.includes('emerald') ? 'from-emerald-500 to-emerald-600' : 
        color?.includes('amber') ? 'from-amber-500 to-amber-600' :
        color?.includes('green') ? 'from-green-500 to-green-600' :
        color?.includes('orange') ? 'from-orange-500 to-orange-600' :
        color?.includes('red') ? 'from-red-500 to-red-600' :
        color?.includes('purple') ? 'from-purple-500 to-purple-600' :
        'from-gray-500 to-gray-600'
      }`}>
        <Icon className="h-4 w-4 text-white" />
      </div>
      <div>
        <p className={`text-2xl font-bold ${color || 'text-gray-900'}`}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        <p className="text-xs font-medium text-gray-500">{title}</p>
        {subValue !== undefined && (
          <p className="text-[10px] text-gray-400 mt-0.5">{subLabel}: {subValue}</p>
        )}
      </div>
    </div>
  </div>
);

/**
 * LeadStats Component
 * 
 * @param {Object} props
 * @param {Object} props.cardCounts - Server-side card counts { all, new_leads, hot, rcb_whatsapp, followup, payment_sent, paid }
 * @param {string} props.activeFilter - Current active filter key
 * @param {function} props.onFilterChange - Callback when a stat card is clicked
 */
export const LeadStats = ({ 
  cardCounts = {},
  activeFilter = 'all', 
  onFilterChange,
}) => {
  const handleFilterClick = (filterKey) => {
    if (onFilterChange) {
      onFilterChange(activeFilter === filterKey ? 'all' : filterKey);
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-5">
      <SummaryCard 
        title="All Leads" 
        value={cardCounts.all || 0} 
        icon={Users} 
        color="text-purple-700" 
        onClick={() => handleFilterClick('all')}
        active={activeFilter === 'all'}
      />
      <SummaryCard 
        title="New Leads" 
        value={cardCounts.new_leads || 0} 
        icon={TrendingUp} 
        color="text-blue-700" 
        onClick={() => handleFilterClick('new_leads')}
        active={activeFilter === 'new_leads'}
      />
      <SummaryCard 
        title="Reminders" 
        value={cardCounts.reminders || 0} 
        icon={Bell} 
        color="text-red-600" 
        onClick={() => handleFilterClick('active_reminders')}
        active={activeFilter === 'active_reminders'}
      />
      <SummaryCard 
        title="RCB WhatsApp" 
        value={cardCounts.rcb_whatsapp || 0} 
        icon={MessageCircle} 
        color="text-green-600" 
        onClick={() => handleFilterClick('rcb_whatsapp')}
        active={activeFilter === 'rcb_whatsapp'}
      />
      <SummaryCard 
        title="Follow Up" 
        value={cardCounts.followup || 0} 
        icon={Bell} 
        color="text-orange-600" 
        onClick={() => handleFilterClick('followup')}
        active={activeFilter === 'followup'}
      />
      <SummaryCard 
        title="Payment Link Sent" 
        value={cardCounts.payment_sent || 0} 
        icon={Link2} 
        color="text-amber-600" 
        onClick={() => handleFilterClick('payment_sent')}
        active={activeFilter === 'payment_sent'}
      />
      <SummaryCard 
        title="Paid" 
        value={cardCounts.paid || 0} 
        subValue={cardCounts.paid_unique}
        subLabel="Unique Sales"
        icon={CheckCircle} 
        color="text-emerald-600" 
        onClick={() => handleFilterClick('paid')}
        active={activeFilter === 'paid'}
      />
    </div>
  );
};

// Also export SummaryCard for reuse
export { SummaryCard };
export default LeadStats;
